'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppNav from '@/components/AppNav';
import { supabase } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────

interface BusinessPassport {
  legal_name: string;
  trade_name: string;
  entity_type: string;
  pan: string | null;
  gstin: string | null;
  founders: Array<{ full_name: string; email: string; pan: string; is_director: boolean }>;
  registered_offices: Array<{ address_line1: string; address_line2: string; city: string; state: string; pincode: string }>;
  business_activities: Array<{ description: string; nic_code: string; industry: string }>;
  business_passports: { completeness_score: number; dpiit_number: string | null } | null;
  date_of_incorporation?: string;
}

interface EligibilityResult {
  eligible: boolean;
  reason: string;
  missingFields: string[];
  passportScore: number;
  recommendation: string;
  warnings: string[];
}

interface DPIITFormData {
  // Step 1 – Entity Details
  legalName: string;
  tradeName: string;
  pan: string;
  entityType: string;
  dateOfIncorporation: string;
  cin: string;
  // Step 2 – Address & Contact
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pincode: string;
  website: string;
  email: string;
  mobile: string;
  // Step 3 – Business Details
  sector: string;
  subSector: string;
  businessDescription: string;
  problemStatement: string;
  uniqueProposition: string;
  stage: string;
  // Step 4 – Financials & GST
  annualTurnover: string;
  turnoverYear: string;
  gstin: string;
  gstLinked: boolean;
  fundingRaised: string;
  fundingSource: string;
  // Step 5 – Founders & Signatory
  founderName: string;
  founderPan: string;
  founderDesignation: string;
  founderEmail: string;
  founderMobile: string;
  founderAadhaar: string;
}

// ─── Demo data ────────────────────────────────────────────────────────────────

const demoPassport: BusinessPassport = {
  legal_name: 'Elysium Fragrances Private Limited',
  trade_name: 'Elysium',
  entity_type: 'private_limited',
  pan: 'AABCE1234F',
  gstin: null,
  founders: [
    { full_name: 'Arjun Sharma', email: 'arjun@elysiumfragrances.com', pan: 'ABCPS1234D', is_director: true },
    { full_name: 'Priya Mehta', email: 'priya@elysiumfragrances.com', pan: 'XYZPM5678E', is_director: true },
  ],
  registered_offices: [{ address_line1: '42, Sector 18', address_line2: 'Noida Industrial Area', city: 'Noida', state: 'Uttar Pradesh', pincode: '201301' }],
  business_activities: [{ description: 'Manufacture and sale of perfumes, cosmetics, and skincare products', nic_code: '20231', industry: 'Manufacturing - Cosmetics' }],
  business_passports: { completeness_score: 72, dpiit_number: null },
  date_of_incorporation: '2022-03-15',
};

const entityTypeLabels: Record<string, string> = {
  private_limited: 'Private Limited Company',
  llp: 'Limited Liability Partnership',
  opc: 'One Person Company',
  partnership: 'Partnership Firm',
  sole_proprietorship: 'Sole Proprietorship',
};

const SECTORS = [
  'AgriTech', 'EdTech', 'FinTech', 'HealthTech', 'CleanTech', 'DeepTech',
  'E-Commerce', 'Enterprise SaaS', 'Consumer Tech', 'Manufacturing & Industry 4.0',
  'Logistics & Supply Chain', 'Media & Entertainment', 'Travel & Hospitality',
  'Social Impact', 'Space Tech', 'Defence Tech', 'Other',
];

const STAGES = [
  { value: 'ideation', label: 'Ideation – Concept stage, no product yet' },
  { value: 'validation', label: 'Validation – MVP built, testing with users' },
  { value: 'early_traction', label: 'Early Traction – Revenue < ₹25L' },
  { value: 'scaling', label: 'Scaling – Revenue ₹25L–₹100Cr' },
];

const STEPS = [
  { id: 1, label: 'Eligibility', icon: '◎' },
  { id: 2, label: 'Entity Details', icon: '◈' },
  { id: 3, label: 'Address & Contact', icon: '⬡' },
  { id: 4, label: 'Business Details', icon: '◇' },
  { id: 5, label: 'Financials & GST', icon: '₹' },
  { id: 6, label: 'Founders', icon: '◉' },
  { id: 7, label: 'Review & Submit', icon: '→' },
];

// ─── Eligibility Logic ────────────────────────────────────────────────────────

function checkEligibility(
  passport: BusinessPassport,
  yearsInOperation: string,
  annualTurnover: string,
): EligibilityResult {
  const missing: string[] = [];
  const warnings: string[] = [];

  if (!passport.legal_name) missing.push('Legal Name');
  if (!passport.pan) missing.push('PAN');
  if (!passport.entity_type) missing.push('Entity Type');
  if (!passport.founders?.length) missing.push('Founder Details');
  if (!passport.registered_offices?.length) missing.push('Registered Address');
  if (!passport.business_activities?.length) missing.push('Business Activity');

  const years = parseFloat(yearsInOperation) || 0;
  const turnover = parseFloat(annualTurnover) || 0;

  // DPIIT eligibility: incorporated ≤ 10 years ago, turnover < ₹100 Cr
  const ageOk = years >= 0 && years <= 10;
  const turnoverOk = turnover < 10000; // in lakhs, so 10000L = 100Cr

  const ineligibleEntity = ['trust', 'society', 'ngo'].includes(passport.entity_type);

  if (ineligibleEntity) {
    return {
      eligible: false,
      reason: `Entity type "${entityTypeLabels[passport.entity_type] || passport.entity_type}" is not eligible for DPIIT Startup India recognition. Eligible types: Pvt Ltd, LLP, OPC, Partnership, Sole Proprietorship.`,
      missingFields: missing,
      passportScore: passport.business_passports?.completeness_score ?? 0,
      recommendation: 'Convert to an eligible entity type before applying.',
      warnings,
    };
  }

  if (!ageOk) {
    return {
      eligible: false,
      reason: `Startup must be incorporated within the last 10 years. Your entity is ${years} years old, which exceeds the limit.`,
      missingFields: missing,
      passportScore: passport.business_passports?.completeness_score ?? 0,
      recommendation: 'DPIIT recognition is only available for startups up to 10 years from date of incorporation.',
      warnings,
    };
  }

  if (!turnoverOk && turnover > 0) {
    return {
      eligible: false,
      reason: `Annual turnover of ₹${(turnover / 100).toFixed(1)} Cr exceeds the ₹100 Cr limit for DPIIT recognition.`,
      missingFields: missing,
      passportScore: passport.business_passports?.completeness_score ?? 0,
      recommendation: 'Startups with turnover above ₹100 Cr are not eligible for DPIIT recognition.',
      warnings,
    };
  }

  if (turnover > 5000) warnings.push('Turnover above ₹50 Cr — ensure you meet the innovation/scalability criteria.');
  if (years > 7) warnings.push('Entity is over 7 years old — approaching the 10-year eligibility limit.');
  if (!passport.gstin) warnings.push('GSTIN not linked — GST registration recommended before applying.');

  if (missing.length > 0) {
    return {
      eligible: false,
      reason: `Business Passport is incomplete. ${missing.length} required field(s) missing.`,
      missingFields: missing,
      passportScore: passport.business_passports?.completeness_score ?? 0,
      recommendation: 'Complete your Business Passport to proceed with the DPIIT application.',
      warnings,
    };
  }

  return {
    eligible: true,
    reason: 'Your startup meets all DPIIT Startup India eligibility criteria.',
    missingFields: [],
    passportScore: passport.business_passports?.completeness_score ?? 0,
    recommendation: 'Proceed to fill the 5-step application form. Most fields are auto-filled from your Business Passport.',
    warnings,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function DPIITPage() {
  const [passport, setPassport] = useState<BusinessPassport>(demoPassport);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Eligibility inputs
  const [yearsInOperation, setYearsInOperation] = useState('');
  const [annualTurnoverFilter, setAnnualTurnoverFilter] = useState('');
  const [eligibilityResult, setEligibilityResult] = useState<EligibilityResult | null>(null);
  const [eligibilityChecked, setEligibilityChecked] = useState(false);

  // GST link
  const [gstinInput, setGstinInput] = useState('');
  const [gstLinkStatus, setGstLinkStatus] = useState<'idle' | 'linking' | 'linked' | 'error'>('idle');

  const [form, setForm] = useState<DPIITFormData>({
    legalName: '', tradeName: '', pan: '', entityType: '', dateOfIncorporation: '',
    cin: '', addressLine1: '', addressLine2: '', city: '', state: '', pincode: '',
    website: '', email: '', mobile: '', sector: '', subSector: '', businessDescription: '',
    problemStatement: '', uniqueProposition: '', stage: '', annualTurnover: '',
    turnoverYear: new Date().getFullYear().toString(), gstin: '', gstLinked: false,
    fundingRaised: '', fundingSource: '', founderName: '', founderPan: '',
    founderDesignation: '', founderEmail: '', founderMobile: '', founderAadhaar: '',
  });

  // Track which fields were auto-filled
  const [autoFilled, setAutoFilled] = useState<Set<string>>(new Set());

  // ─── Load passport ──────────────────────────────────────────────────────────

  useEffect(() => {
    async function load() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: bp } = await supabase
            .from('businesses')
            .select(`
              legal_name, trade_name, entity_type, pan, gstin,
              founders(full_name, email, pan, is_director),
              registered_offices(address_line1, address_line2, city, state, pincode),
              business_activities(description, nic_code, industry),
              business_passports(completeness_score, dpiit_number),
              date_of_incorporation
            `)
            .eq('user_id', user.id)
            .single();
          if (bp) setPassport(bp as BusinessPassport);
        }
      } catch {
        // use demo data
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // ─── Auto-fill from passport ────────────────────────────────────────────────

  function autoFillFromPassport() {
    const office = passport.registered_offices?.[0];
    const activity = passport.business_activities?.[0];
    const founder = passport.founders?.find(f => f.is_director) ?? passport.founders?.[0];
    const filled = new Set<string>();

    const updates: Partial<DPIITFormData> = {};

    if (passport.legal_name) { updates.legalName = passport.legal_name; filled.add('legalName'); }
    if (passport.trade_name) { updates.tradeName = passport.trade_name; filled.add('tradeName'); }
    if (passport.pan) { updates.pan = passport.pan; filled.add('pan'); }
    if (passport.entity_type) { updates.entityType = passport.entity_type; filled.add('entityType'); }
    if (passport.date_of_incorporation) { updates.dateOfIncorporation = passport.date_of_incorporation; filled.add('dateOfIncorporation'); }
    if (passport.gstin) { updates.gstin = passport.gstin; updates.gstLinked = true; filled.add('gstin'); filled.add('gstLinked'); }
    if (office) {
      if (office.address_line1) { updates.addressLine1 = office.address_line1; filled.add('addressLine1'); }
      if (office.address_line2) { updates.addressLine2 = office.address_line2; filled.add('addressLine2'); }
      if (office.city) { updates.city = office.city; filled.add('city'); }
      if (office.state) { updates.state = office.state; filled.add('state'); }
      if (office.pincode) { updates.pincode = office.pincode; filled.add('pincode'); }
    }
    if (activity) {
      if (activity.description) { updates.businessDescription = activity.description; filled.add('businessDescription'); }
      if (activity.industry) { updates.sector = activity.industry.split(' - ')[0] || ''; filled.add('sector'); }
    }
    if (founder) {
      if (founder.full_name) { updates.founderName = founder.full_name; filled.add('founderName'); }
      if (founder.pan) { updates.founderPan = founder.pan; filled.add('founderPan'); }
      if (founder.email) { updates.founderEmail = founder.email; filled.add('founderEmail'); }
      updates.founderDesignation = 'Director'; filled.add('founderDesignation');
    }

    setForm(prev => ({ ...prev, ...updates }));
    setAutoFilled(filled);
  }

  // ─── Eligibility check ──────────────────────────────────────────────────────

  function handleCheckEligibility() {
    const result = checkEligibility(passport, yearsInOperation, annualTurnoverFilter);
    setEligibilityResult(result);
    setEligibilityChecked(true);
    if (result.eligible) {
      autoFillFromPassport();
    }
  }

  // ─── GST link ───────────────────────────────────────────────────────────────

  async function handleGSTLink() {
    if (!gstinInput || gstinInput.length !== 15) return;
    setGstLinkStatus('linking');
    await new Promise(r => setTimeout(r, 1200));
    setForm(prev => ({ ...prev, gstin: gstinInput, gstLinked: true }));
    setAutoFilled(prev => new Set([...prev, 'gstin', 'gstLinked']));
    setGstLinkStatus('linked');
  }

  // ─── Form helpers ───────────────────────────────────────────────────────────

  function setField(key: keyof DPIITFormData, value: string | boolean) {
    setForm(prev => ({ ...prev, [key]: value }));
    setAutoFilled(prev => { const n = new Set(prev); n.delete(key); return n; });
  }

  function AutoBadge({ field }: { field: string }) {
    if (!autoFilled.has(field)) return null;
    return (
      <span className="ml-2 text-[10px] font-semibold text-ai bg-ai-light px-1.5 py-0.5 rounded-full">
        ◈ Auto-filled
      </span>
    );
  }

  function InputField({
    label, field, type = 'text', placeholder = '', required = false,
    options, textarea = false, hint,
  }: {
    label: string; field: keyof DPIITFormData; type?: string; placeholder?: string;
    required?: boolean; options?: { value: string; label: string }[]; textarea?: boolean; hint?: string;
  }) {
    const value = form[field] as string;
    const isFilled = autoFilled.has(field);
    const baseClass = `w-full px-3 py-2.5 rounded-lg border text-[13px] text-ink bg-white transition-all outline-none focus:ring-2 focus:ring-ai/20 ${
      isFilled ? 'border-ai/40 bg-ai-light/30' : 'border-border focus:border-ai/50'
    }`;

    return (
      <div>
        <label className="block text-[12px] font-semibold text-ink-muted mb-1.5 uppercase tracking-wide">
          {label} {required && <span className="text-error">*</span>}
          <AutoBadge field={field} />
        </label>
        {options ? (
          <select className={baseClass} value={value} onChange={e => setField(field, e.target.value)}>
            <option value="">Select {label}</option>
            {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : textarea ? (
          <textarea
            className={`${baseClass} resize-none`}
            rows={3}
            placeholder={placeholder}
            value={value}
            onChange={e => setField(field, e.target.value)}
          />
        ) : (
          <input
            type={type}
            className={baseClass}
            placeholder={placeholder}
            value={value}
            onChange={e => setField(field, e.target.value)}
          />
        )}
        {hint && <p className="text-[11px] text-ink-muted mt-1">{hint}</p>}
      </div>
    );
  }

  // ─── Submit ─────────────────────────────────────────────────────────────────

  async function handleSubmit() {
    setSubmitting(true);
    await new Promise(r => setTimeout(r, 2000));
    setSubmitting(false);
    setSubmitted(true);
  }

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex min-h-screen bg-surface">
        <AppNav activePath="/dpiit" />
        <main className="sm:ml-60 flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-10 h-10 border-2 border-ai border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-[13px] text-ink-muted">Loading Business Passport…</p>
          </div>
        </main>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex min-h-screen bg-surface">
        <AppNav activePath="/dpiit" />
        <main className="sm:ml-60 flex-1 flex items-center justify-center p-6">
          <div className="max-w-lg w-full text-center">
            <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">🏆</span>
            </div>
            <h2 className="text-2xl font-bold text-ink mb-2">Application Submitted!</h2>
            <p className="text-ink-muted text-[14px] mb-6">
              Your DPIIT Startup India recognition application has been submitted. You will receive a confirmation on your registered email within 2–3 business days.
            </p>
            <div className="bg-surface-2 border border-border rounded-xl p-5 text-left mb-6 space-y-3">
              <div className="flex justify-between text-[13px]">
                <span className="text-ink-muted">Application Reference</span>
                <span className="font-semibold text-ink">DPIIT-2024-{Math.floor(Math.random() * 90000 + 10000)}</span>
              </div>
              <div className="flex justify-between text-[13px]">
                <span className="text-ink-muted">Entity</span>
                <span className="font-semibold text-ink">{form.legalName || passport.legal_name}</span>
              </div>
              <div className="flex justify-between text-[13px]">
                <span className="text-ink-muted">Status</span>
                <span className="font-semibold text-warning">Under Review</span>
              </div>
              <div className="flex justify-between text-[13px]">
                <span className="text-ink-muted">Expected TAT</span>
                <span className="font-semibold text-ink">2–3 Business Days</span>
              </div>
            </div>
            <div className="flex gap-3 justify-center">
              <Link href="/dashboard" className="px-5 py-2.5 bg-ai text-white text-[13px] font-semibold rounded-lg hover:bg-ai-dark transition-colors">
                Go to Dashboard
              </Link>
              <button onClick={() => { setSubmitted(false); setCurrentStep(1); setEligibilityChecked(false); setEligibilityResult(null); }}
                className="px-5 py-2.5 border border-border text-ink-muted text-[13px] font-medium rounded-lg hover:bg-surface-2 transition-colors">
                New Application
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const passportScore = passport.business_passports?.completeness_score ?? 0;
  const autoFilledCount = autoFilled.size;

  return (
    <div className="flex min-h-screen bg-surface">
      <AppNav activePath="/dpiit" />

      <main className="sm:ml-60 flex-1 pt-14 sm:pt-0">
        {/* Header */}
        <div className="bg-white border-b border-border px-6 py-5">
          <div className="max-w-5xl mx-auto flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xl">🇮🇳</span>
                <h1 className="text-xl font-bold text-ink">DPIIT Startup India Recognition</h1>
                <span className="text-[11px] font-semibold text-success bg-success/10 px-2 py-0.5 rounded-full">₹0 Govt Fee</span>
              </div>
              <p className="text-[13px] text-ink-muted">
                Get recognized as a DPIIT-registered startup — unlock tax benefits, funding access, and fast-track compliance.
              </p>
            </div>
            <div className="hidden md:flex items-center gap-4 shrink-0">
              <div className="text-right">
                <p className="text-[11px] text-ink-muted uppercase tracking-wide font-semibold">Passport Score</p>
                <p className={`text-lg font-bold ${passportScore >= 70 ? 'text-success' : passportScore >= 40 ? 'text-warning' : 'text-error'}`}>
                  {passportScore}%
                </p>
              </div>
              {autoFilledCount > 0 && (
                <div className="text-right">
                  <p className="text-[11px] text-ink-muted uppercase tracking-wide font-semibold">Auto-filled</p>
                  <p className="text-lg font-bold text-ai">{autoFilledCount} fields</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 py-6">
          {/* Benefits Banner */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[
              { icon: '🏦', title: 'Tax Exemption', desc: '3-year income tax holiday (Sec 80-IAC)' },
              { icon: '⚡', title: 'Fast-track IPR', desc: '80% rebate on patent filing fees' },
              { icon: '🤝', title: 'Funding Access', desc: 'SIDBI Fund of Funds, Angel Tax exemption' },
              { icon: '📋', title: 'Self-certify', desc: '9 labour & environment laws compliance' },
            ].map(b => (
              <div key={b.title} className="bg-white border border-border rounded-xl p-3.5">
                <div className="text-xl mb-1.5">{b.icon}</div>
                <p className="text-[12px] font-semibold text-ink">{b.title}</p>
                <p className="text-[11px] text-ink-muted mt-0.5">{b.desc}</p>
              </div>
            ))}
          </div>

          {/* Step Indicator */}
          <div className="bg-white border border-border rounded-xl p-4 mb-6 overflow-x-auto">
            <div className="flex items-center gap-1 min-w-max">
              {STEPS.map((step, idx) => (
                <React.Fragment key={step.id}>
                  <button
                    onClick={() => eligibilityChecked && eligibilityResult?.eligible && setCurrentStep(step.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg text-[12px] font-medium transition-all ${
                      currentStep === step.id
                        ? 'bg-ai text-white'
                        : currentStep > step.id
                        ? 'bg-success/10 text-success' :'text-ink-muted'
                    }`}
                  >
                    <span>{currentStep > step.id ? '✓' : step.icon}</span>
                    <span className="hidden sm:inline">{step.label}</span>
                    <span className="sm:hidden">{step.id}</span>
                  </button>
                  {idx < STEPS.length - 1 && (
                    <div className={`h-px w-4 shrink-0 ${currentStep > step.id ? 'bg-success' : 'bg-border'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* ── Step 1: Eligibility ── */}
          {currentStep === 1 && (
            <div className="grid lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2 space-y-5">
                {/* Eligibility Checker */}
                <div className="bg-white border border-border rounded-xl p-5">
                  <h2 className="text-[15px] font-bold text-ink mb-1">Eligibility Checker</h2>
                  <p className="text-[12px] text-ink-muted mb-5">
                    DPIIT recognition requires: incorporated ≤ 10 years ago, annual turnover &lt; ₹100 Cr, working towards innovation/scalability.
                  </p>

                  <div className="grid sm:grid-cols-2 gap-4 mb-5">
                    {/* Years in operation */}
                    <div>
                      <label className="block text-[12px] font-semibold text-ink-muted mb-1.5 uppercase tracking-wide">
                        Years in Operation <span className="text-error">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min="0"
                          max="15"
                          step="0.5"
                          placeholder="e.g. 2.5"
                          value={yearsInOperation}
                          onChange={e => setYearsInOperation(e.target.value)}
                          className="w-full px-3 py-2.5 pr-14 rounded-lg border border-border text-[13px] text-ink bg-white outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/50"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-ink-muted font-medium">years</span>
                      </div>
                      <p className="text-[11px] text-ink-muted mt-1">From date of incorporation to today</p>
                    </div>

                    {/* Annual Turnover */}
                    <div>
                      <label className="block text-[12px] font-semibold text-ink-muted mb-1.5 uppercase tracking-wide">
                        Annual Turnover (₹ Lakhs)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-ink-muted font-medium">₹</span>
                        <input
                          type="number"
                          min="0"
                          placeholder="0 if pre-revenue"
                          value={annualTurnoverFilter}
                          onChange={e => setAnnualTurnoverFilter(e.target.value)}
                          className="w-full pl-7 pr-14 py-2.5 rounded-lg border border-border text-[13px] text-ink bg-white outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/50"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-ink-muted font-medium">L</span>
                      </div>
                      <p className="text-[11px] text-ink-muted mt-1">Must be below ₹100 Cr (10,000 Lakhs)</p>
                    </div>
                  </div>

                  {/* Turnover filter visual */}
                  {annualTurnoverFilter && (
                    <div className="mb-5 p-3 bg-surface-2 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[12px] font-semibold text-ink-muted">Turnover Threshold</span>
                        <span className={`text-[12px] font-bold ${parseFloat(annualTurnoverFilter) < 10000 ? 'text-success' : 'text-error'}`}>
                          {parseFloat(annualTurnoverFilter) < 10000 ? '✓ Within limit' : '✗ Exceeds limit'}
                        </span>
                      </div>
                      <div className="h-2 bg-border rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${parseFloat(annualTurnoverFilter) < 10000 ? 'bg-success' : 'bg-error'}`}
                          style={{ width: `${Math.min((parseFloat(annualTurnoverFilter) / 10000) * 100, 100)}%` }}
                        />
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-[10px] text-ink-muted">₹0</span>
                        <span className="text-[10px] text-ink-muted">₹100 Cr limit</span>
                      </div>
                    </div>
                  )}

                  {/* Entity type check */}
                  <div className="mb-5 p-3 bg-surface-2 rounded-lg">
                    <p className="text-[12px] font-semibold text-ink mb-2">Entity Type Check</p>
                    <div className="flex items-center gap-2">
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                        ['private_limited', 'llp', 'opc', 'partnership', 'sole_proprietorship'].includes(passport.entity_type)
                          ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                      }`}>
                        {['private_limited', 'llp', 'opc', 'partnership', 'sole_proprietorship'].includes(passport.entity_type) ? '✓' : '✗'}
                      </span>
                      <span className="text-[13px] text-ink">
                        {entityTypeLabels[passport.entity_type] || passport.entity_type || 'Not set'}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={handleCheckEligibility}
                    disabled={!yearsInOperation}
                    className="w-full py-3 bg-ai text-white text-[13px] font-semibold rounded-lg hover:bg-ai-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Check Eligibility
                  </button>
                </div>

                {/* Eligibility Result */}
                {eligibilityResult && (
                  <div className={`border rounded-xl p-5 ${
                    eligibilityResult.eligible
                      ? 'bg-success/5 border-success/30' :'bg-error/5 border-error/30'
                  }`}>
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl shrink-0 ${
                        eligibilityResult.eligible ? 'bg-success/10' : 'bg-error/10'
                      }`}>
                        {eligibilityResult.eligible ? '✅' : '❌'}
                      </div>
                      <div>
                        <p className={`text-[14px] font-bold ${eligibilityResult.eligible ? 'text-success' : 'text-error'}`}>
                          {eligibilityResult.eligible ? 'Eligible for DPIIT Recognition' : 'Not Currently Eligible'}
                        </p>
                        <p className="text-[13px] text-ink-muted mt-0.5">{eligibilityResult.reason}</p>
                      </div>
                    </div>

                    {eligibilityResult.warnings.length > 0 && (
                      <div className="mb-3 space-y-1.5">
                        {eligibilityResult.warnings.map((w, i) => (
                          <div key={i} className="flex items-start gap-2 p-2.5 bg-warning/10 border border-warning/20 rounded-lg">
                            <span className="text-warning text-[12px] mt-0.5">⚠</span>
                            <p className="text-[12px] text-ink">{w}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {eligibilityResult.missingFields.length > 0 && (
                      <div className="mb-3 p-3 bg-white/60 rounded-lg">
                        <p className="text-[12px] font-semibold text-ink mb-2">Missing from Business Passport:</p>
                        <div className="flex flex-wrap gap-1.5">
                          {eligibilityResult.missingFields.map(f => (
                            <span key={f} className="text-[11px] font-medium bg-error/10 text-error px-2 py-0.5 rounded-full">{f}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    <p className="text-[12px] text-ink-muted italic">{eligibilityResult.recommendation}</p>

                    {eligibilityResult.eligible && (
                      <button
                        onClick={() => setCurrentStep(2)}
                        className="mt-4 w-full py-2.5 bg-ai text-white text-[13px] font-semibold rounded-lg hover:bg-ai-dark transition-colors"
                      >
                        Proceed to Application →
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Right panel */}
              <div className="space-y-4">
                {/* Passport completeness */}
                <div className="bg-white border border-border rounded-xl p-4">
                  <p className="text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-3">Business Passport</p>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-full bg-surface-2 border-4 border-ai/20 flex items-center justify-center">
                      <span className={`text-[13px] font-bold ${passportScore >= 70 ? 'text-success' : passportScore >= 40 ? 'text-warning' : 'text-error'}`}>
                        {passportScore}%
                      </span>
                    </div>
                    <div>
                      <p className="text-[13px] font-semibold text-ink">{passport.legal_name || 'Your Business'}</p>
                      <p className="text-[11px] text-ink-muted">{entityTypeLabels[passport.entity_type] || 'Entity type not set'}</p>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    {[
                      { label: 'Legal Name', ok: !!passport.legal_name },
                      { label: 'PAN', ok: !!passport.pan },
                      { label: 'Entity Type', ok: !!passport.entity_type },
                      { label: 'Registered Address', ok: !!passport.registered_offices?.length },
                      { label: 'Business Activity', ok: !!passport.business_activities?.length },
                      { label: 'Founder Details', ok: !!passport.founders?.length },
                      { label: 'GSTIN', ok: !!passport.gstin },
                    ].map(item => (
                      <div key={item.label} className="flex items-center justify-between">
                        <span className="text-[12px] text-ink-muted">{item.label}</span>
                        <span className={`text-[11px] font-semibold ${item.ok ? 'text-success' : 'text-error'}`}>
                          {item.ok ? '✓' : '✗ Missing'}
                        </span>
                      </div>
                    ))}
                  </div>
                  <Link href="/business" className="mt-3 block text-center text-[12px] font-semibold text-ai hover:underline">
                    Complete Business Passport →
                  </Link>
                </div>

                {/* DPIIT criteria */}
                <div className="bg-white border border-border rounded-xl p-4">
                  <p className="text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-3">DPIIT Criteria</p>
                  <div className="space-y-2.5">
                    {[
                      { label: 'Incorporated ≤ 10 years ago', detail: 'From date of incorporation' },
                      { label: 'Annual turnover < ₹100 Cr', detail: 'In any financial year' },
                      { label: 'Working towards innovation', detail: 'New product/process/service' },
                      { label: 'Scalable business model', detail: 'High employment/wealth potential' },
                      { label: 'Not formed by splitting', detail: 'No restructuring of existing biz' },
                    ].map(c => (
                      <div key={c.label} className="flex items-start gap-2">
                        <span className="text-ai text-[12px] mt-0.5">◈</span>
                        <div>
                          <p className="text-[12px] font-medium text-ink">{c.label}</p>
                          <p className="text-[11px] text-ink-muted">{c.detail}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Step 2: Entity Details ── */}
          {currentStep === 2 && (
            <div className="bg-white border border-border rounded-xl p-6">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-[15px] font-bold text-ink">Entity Details</h2>
                  <p className="text-[12px] text-ink-muted mt-0.5">Basic information about your startup entity</p>
                </div>
                {autoFilledCount > 0 && (
                  <span className="text-[11px] font-semibold text-ai bg-ai-light px-2.5 py-1 rounded-full">
                    ◈ {autoFilledCount} fields auto-filled
                  </span>
                )}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <InputField label="Legal Name" field="legalName" required placeholder="As per MCA records" />
                <InputField label="Trade / Brand Name" field="tradeName" placeholder="If different from legal name" />
                <InputField label="PAN" field="pan" required placeholder="AAAAA0000A" />
                <InputField
                  label="Entity Type"
                  field="entityType"
                  required
                  options={Object.entries(entityTypeLabels).map(([v, l]) => ({ value: v, label: l }))}
                />
                <InputField label="Date of Incorporation" field="dateOfIncorporation" type="date" required />
                <InputField label="CIN / LLPIN" field="cin" placeholder="L12345MH2022PTC000000" hint="Corporate Identity Number from MCA" />
              </div>
              <div className="flex justify-between mt-6">
                <button onClick={() => setCurrentStep(1)} className="px-5 py-2.5 border border-border text-ink-muted text-[13px] font-medium rounded-lg hover:bg-surface-2 transition-colors">
                  ← Back
                </button>
                <button onClick={() => setCurrentStep(3)} className="px-5 py-2.5 bg-ai text-white text-[13px] font-semibold rounded-lg hover:bg-ai-dark transition-colors">
                  Next: Address →
                </button>
              </div>
            </div>
          )}

          {/* ── Step 3: Address & Contact ── */}
          {currentStep === 3 && (
            <div className="bg-white border border-border rounded-xl p-6">
              <h2 className="text-[15px] font-bold text-ink mb-1">Address & Contact</h2>
              <p className="text-[12px] text-ink-muted mb-5">Registered office address and primary contact details</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <InputField label="Address Line 1" field="addressLine1" required placeholder="Building, Street" />
                </div>
                <div className="sm:col-span-2">
                  <InputField label="Address Line 2" field="addressLine2" placeholder="Area, Locality" />
                </div>
                <InputField label="City" field="city" required placeholder="City" />
                <InputField label="State" field="state" required placeholder="State" />
                <InputField label="Pincode" field="pincode" required placeholder="110001" />
                <InputField label="Website" field="website" type="url" placeholder="https://yourstartup.com" />
                <InputField label="Primary Email" field="email" type="email" required placeholder="contact@startup.com" />
                <InputField label="Primary Mobile" field="mobile" type="tel" required placeholder="+91 98765 43210" />
              </div>
              <div className="flex justify-between mt-6">
                <button onClick={() => setCurrentStep(2)} className="px-5 py-2.5 border border-border text-ink-muted text-[13px] font-medium rounded-lg hover:bg-surface-2 transition-colors">
                  ← Back
                </button>
                <button onClick={() => setCurrentStep(4)} className="px-5 py-2.5 bg-ai text-white text-[13px] font-semibold rounded-lg hover:bg-ai-dark transition-colors">
                  Next: Business Details →
                </button>
              </div>
            </div>
          )}

          {/* ── Step 4: Business Details ── */}
          {currentStep === 4 && (
            <div className="bg-white border border-border rounded-xl p-6">
              <h2 className="text-[15px] font-bold text-ink mb-1">Business Details</h2>
              <p className="text-[12px] text-ink-muted mb-5">Describe your startup's innovation and business model</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <InputField
                  label="Sector"
                  field="sector"
                  required
                  options={SECTORS.map(s => ({ value: s, label: s }))}
                />
                <InputField label="Sub-sector / Niche" field="subSector" placeholder="e.g. Luxury Fragrance D2C" />
                <div className="sm:col-span-2">
                  <InputField
                    label="Business Description"
                    field="businessDescription"
                    required
                    textarea
                    placeholder="Describe what your startup does (max 500 chars)"
                  />
                </div>
                <div className="sm:col-span-2">
                  <InputField
                    label="Problem Statement"
                    field="problemStatement"
                    required
                    textarea
                    placeholder="What problem does your startup solve?"
                  />
                </div>
                <div className="sm:col-span-2">
                  <InputField
                    label="Unique Value Proposition"
                    field="uniqueProposition"
                    required
                    textarea
                    placeholder="What makes your solution unique/innovative?"
                  />
                </div>
                <div className="sm:col-span-2">
                  <InputField
                    label="Current Stage"
                    field="stage"
                    required
                    options={STAGES}
                  />
                </div>
              </div>
              <div className="flex justify-between mt-6">
                <button onClick={() => setCurrentStep(3)} className="px-5 py-2.5 border border-border text-ink-muted text-[13px] font-medium rounded-lg hover:bg-surface-2 transition-colors">
                  ← Back
                </button>
                <button onClick={() => setCurrentStep(5)} className="px-5 py-2.5 bg-ai text-white text-[13px] font-semibold rounded-lg hover:bg-ai-dark transition-colors">
                  Next: Financials →
                </button>
              </div>
            </div>
          )}

          {/* ── Step 5: Financials & GST ── */}
          {currentStep === 5 && (
            <div className="bg-white border border-border rounded-xl p-6">
              <h2 className="text-[15px] font-bold text-ink mb-1">Financials & GST</h2>
              <p className="text-[12px] text-ink-muted mb-5">Turnover details and GST registration linkage</p>

              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-[12px] font-semibold text-ink-muted mb-1.5 uppercase tracking-wide">
                    Annual Turnover (₹ Lakhs) <span className="text-error">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px] text-ink-muted">₹</span>
                    <input
                      type="number"
                      min="0"
                      placeholder="0 if pre-revenue"
                      value={form.annualTurnover}
                      onChange={e => setField('annualTurnover', e.target.value)}
                      className="w-full pl-7 pr-14 py-2.5 rounded-lg border border-border text-[13px] text-ink bg-white outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/50"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-ink-muted">Lakhs</span>
                  </div>
                </div>
                <InputField
                  label="Financial Year"
                  field="turnoverYear"
                  options={['2024-25', '2023-24', '2022-23', '2021-22'].map(y => ({ value: y, label: `FY ${y}` }))}
                />
                <InputField label="Funding Raised (₹ Lakhs)" field="fundingRaised" type="number" placeholder="0 if bootstrapped" />
                <InputField
                  label="Funding Source"
                  field="fundingSource"
                  options={[
                    { value: 'bootstrapped', label: 'Bootstrapped' },
                    { value: 'angel', label: 'Angel Investment' },
                    { value: 'vc', label: 'Venture Capital' },
                    { value: 'govt', label: 'Government Grant' },
                    { value: 'debt', label: 'Debt / Loan' },
                    { value: 'other', label: 'Other' },
                  ]}
                />
              </div>

              {/* GST Auto-Link */}
              <div className="border border-border rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-base">🔗</span>
                  <p className="text-[13px] font-bold text-ink">GST Auto-Link</p>
                  {form.gstLinked && (
                    <span className="text-[11px] font-semibold text-success bg-success/10 px-2 py-0.5 rounded-full">Linked</span>
                  )}
                </div>

                {form.gstLinked ? (
                  <div className="flex items-center gap-3 p-3 bg-success/5 border border-success/20 rounded-lg">
                    <span className="text-success text-lg">✓</span>
                    <div>
                      <p className="text-[13px] font-semibold text-ink">{form.gstin}</p>
                      <p className="text-[11px] text-ink-muted">GSTIN linked successfully — turnover data will be auto-verified</p>
                    </div>
                    <button
                      onClick={() => { setForm(prev => ({ ...prev, gstin: '', gstLinked: false })); setGstLinkStatus('idle'); setGstinInput(''); }}
                      className="ml-auto text-[11px] text-error hover:underline"
                    >
                      Unlink
                    </button>
                  </div>
                ) : (
                  <div>
                    <p className="text-[12px] text-ink-muted mb-3">
                      Link your GSTIN to auto-verify turnover and pre-fill GST details in the application.
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        maxLength={15}
                        placeholder="Enter 15-digit GSTIN"
                        value={gstinInput}
                        onChange={e => setGstinInput(e.target.value.toUpperCase())}
                        className="flex-1 px-3 py-2.5 rounded-lg border border-border text-[13px] text-ink bg-white outline-none focus:ring-2 focus:ring-ai/20 focus:border-ai/50 font-mono"
                      />
                      <button
                        onClick={handleGSTLink}
                        disabled={gstinInput.length !== 15 || gstLinkStatus === 'linking'}
                        className="px-4 py-2.5 bg-ai text-white text-[13px] font-semibold rounded-lg hover:bg-ai-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {gstLinkStatus === 'linking' ? (
                          <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Linking…</>
                        ) : 'Link GST'}
                      </button>
                    </div>
                    {gstLinkStatus === 'error' && (
                      <p className="text-[12px] text-error mt-2">Could not verify GSTIN. Please check and try again.</p>
                    )}
                    <p className="text-[11px] text-ink-muted mt-2">
                      Don't have GST? <Link href="/gst" className="text-ai hover:underline">Register for GST first →</Link>
                    </p>
                  </div>
                )}
              </div>

              <div className="flex justify-between mt-6">
                <button onClick={() => setCurrentStep(4)} className="px-5 py-2.5 border border-border text-ink-muted text-[13px] font-medium rounded-lg hover:bg-surface-2 transition-colors">
                  ← Back
                </button>
                <button onClick={() => setCurrentStep(6)} className="px-5 py-2.5 bg-ai text-white text-[13px] font-semibold rounded-lg hover:bg-ai-dark transition-colors">
                  Next: Founders →
                </button>
              </div>
            </div>
          )}

          {/* ── Step 6: Founders ── */}
          {currentStep === 6 && (
            <div className="bg-white border border-border rounded-xl p-6">
              <h2 className="text-[15px] font-bold text-ink mb-1">Founder / Authorized Signatory</h2>
              <p className="text-[12px] text-ink-muted mb-5">Details of the primary founder or authorized representative</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <InputField label="Full Name" field="founderName" required placeholder="As per PAN" />
                <InputField label="Designation" field="founderDesignation" required placeholder="Director / Partner / Proprietor" />
                <InputField label="PAN" field="founderPan" required placeholder="AAAAA0000A" />
                <InputField label="Aadhaar (last 4 digits)" field="founderAadhaar" placeholder="XXXX" hint="Only last 4 digits required" />
                <InputField label="Email" field="founderEmail" type="email" required placeholder="founder@startup.com" />
                <InputField label="Mobile" field="founderMobile" type="tel" required placeholder="+91 98765 43210" />
              </div>

              {/* Founders from passport */}
              {passport.founders && passport.founders.length > 1 && (
                <div className="mt-5 p-4 bg-surface-2 rounded-xl">
                  <p className="text-[12px] font-semibold text-ink-muted uppercase tracking-wide mb-3">Other Founders (from Business Passport)</p>
                  <div className="space-y-2">
                    {passport.founders.slice(1).map((f, i) => (
                      <div key={i} className="flex items-center justify-between p-2.5 bg-white border border-border rounded-lg">
                        <div>
                          <p className="text-[13px] font-medium text-ink">{f.full_name}</p>
                          <p className="text-[11px] text-ink-muted">{f.email} · PAN: {f.pan}</p>
                        </div>
                        <button
                          onClick={() => {
                            setField('founderName', f.full_name);
                            setField('founderPan', f.pan);
                            setField('founderEmail', f.email);
                            setField('founderDesignation', f.is_director ? 'Director' : 'Partner');
                          }}
                          className="text-[11px] font-semibold text-ai hover:underline"
                        >
                          Use this
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-between mt-6">
                <button onClick={() => setCurrentStep(5)} className="px-5 py-2.5 border border-border text-ink-muted text-[13px] font-medium rounded-lg hover:bg-surface-2 transition-colors">
                  ← Back
                </button>
                <button onClick={() => setCurrentStep(7)} className="px-5 py-2.5 bg-ai text-white text-[13px] font-semibold rounded-lg hover:bg-ai-dark transition-colors">
                  Review Application →
                </button>
              </div>
            </div>
          )}

          {/* ── Step 7: Review & Submit ── */}
          {currentStep === 7 && (
            <div className="space-y-5">
              <div className="bg-white border border-border rounded-xl p-5">
                <h2 className="text-[15px] font-bold text-ink mb-4">Review Your Application</h2>

                {/* Summary sections */}
                {[
                  {
                    title: 'Entity Details', step: 2, fields: [
                      { label: 'Legal Name', value: form.legalName },
                      { label: 'Trade Name', value: form.tradeName },
                      { label: 'PAN', value: form.pan },
                      { label: 'Entity Type', value: entityTypeLabels[form.entityType] || form.entityType },
                      { label: 'Date of Incorporation', value: form.dateOfIncorporation },
                      { label: 'CIN', value: form.cin },
                    ],
                  },
                  {
                    title: 'Address & Contact', step: 3, fields: [
                      { label: 'Address', value: [form.addressLine1, form.addressLine2, form.city, form.state, form.pincode].filter(Boolean).join(', ') },
                      { label: 'Email', value: form.email },
                      { label: 'Mobile', value: form.mobile },
                      { label: 'Website', value: form.website },
                    ],
                  },
                  {
                    title: 'Business Details', step: 4, fields: [
                      { label: 'Sector', value: form.sector },
                      { label: 'Stage', value: STAGES.find(s => s.value === form.stage)?.label || form.stage },
                      { label: 'Description', value: form.businessDescription },
                    ],
                  },
                  {
                    title: 'Financials & GST', step: 5, fields: [
                      { label: 'Annual Turnover', value: form.annualTurnover ? `₹${form.annualTurnover} Lakhs (FY ${form.turnoverYear})` : 'Pre-revenue' },
                      { label: 'Funding Raised', value: form.fundingRaised ? `₹${form.fundingRaised} Lakhs (${form.fundingSource})` : 'Bootstrapped' },
                      { label: 'GSTIN', value: form.gstLinked ? `${form.gstin} ✓ Linked` : 'Not linked' },
                    ],
                  },
                  {
                    title: 'Founder / Signatory', step: 6, fields: [
                      { label: 'Name', value: form.founderName },
                      { label: 'Designation', value: form.founderDesignation },
                      { label: 'PAN', value: form.founderPan },
                      { label: 'Email', value: form.founderEmail },
                      { label: 'Mobile', value: form.founderMobile },
                    ],
                  },
                ].map(section => (
                  <div key={section.title} className="mb-4 last:mb-0">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-[12px] font-bold text-ink-muted uppercase tracking-wide">{section.title}</p>
                      <button onClick={() => setCurrentStep(section.step)} className="text-[11px] font-semibold text-ai hover:underline">
                        Edit
                      </button>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 p-3 bg-surface-2 rounded-lg">
                      {section.fields.filter(f => f.value).map(f => (
                        <div key={f.label} className="flex items-start gap-2">
                          <span className="text-[11px] text-ink-muted w-28 shrink-0">{f.label}</span>
                          <span className="text-[12px] font-medium text-ink">{f.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Declaration */}
              <div className="bg-white border border-border rounded-xl p-5">
                <p className="text-[13px] font-bold text-ink mb-3">Declaration</p>
                <div className="p-3 bg-surface-2 rounded-lg text-[12px] text-ink-muted leading-relaxed mb-4">
                  I hereby declare that the information provided above is true and correct to the best of my knowledge. I understand that providing false information may result in cancellation of the DPIIT recognition and legal action as per applicable laws.
                </div>
                <div className="flex items-start gap-2">
                  <input type="checkbox" id="declaration" className="mt-0.5 accent-ai" />
                  <label htmlFor="declaration" className="text-[12px] text-ink cursor-pointer">
                    I agree to the above declaration and confirm that all information is accurate.
                  </label>
                </div>
              </div>

              <div className="flex justify-between">
                <button onClick={() => setCurrentStep(6)} className="px-5 py-2.5 border border-border text-ink-muted text-[13px] font-medium rounded-lg hover:bg-surface-2 transition-colors">
                  ← Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="px-6 py-2.5 bg-ai text-white text-[13px] font-semibold rounded-lg hover:bg-ai-dark transition-colors disabled:opacity-70 flex items-center gap-2"
                >
                  {submitting ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Submitting…</>
                  ) : '🚀 Submit to DPIIT'}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
